import React, {useEffect, useRef} from "react";
import {SiteMessageEnum} from "@varymadellc/my-weed-club-component-library";
import {AiFillDelete, AiFillMessage, AiFillPicture} from "react-icons/ai";
import {Img} from "@varymadellc/my-weed-club-component-library";
import {Card} from "@varymadellc/my-weed-club-component-library";
import {useSiteMessage} from "@/providers/SiteMessageProvider";
import imagePathFromFile from "@/utils/imagePathFromFile";
import {deleteAccount} from "@/services/profileService";
import {Account, Membership} from "@/types";
import {useAuth} from "@/providers/AuthProvider";
import {useRouter} from "next/navigation";
import Link from "next/link";
import ProfileBadges from "@/components/ProfileBadges";
import MediaCenterBox from "@/components/ImageCenter/MediaCenterBox";

interface ProfileItemProps {
    profile: Account;
    memberships?: Membership[];
    deleteProfileCallback?: (uuid: string) => void;
}

function membershipPlanToName(plan?: string) {
    switch (plan) {
        case 'price_1NAiVnFj70g08qfGHRJgaF0z':
            return 'Gas 89';
        case 'price_1NAiVJFj70g08qfGbIsMrqiq':
            return 'Gas 91';
        default:
            return 'Unknown';
    }
}

export default function ProfileItem({profile, memberships, deleteProfileCallback}: ProfileItemProps) {
    const [showImage, setShowImage] = React.useState<boolean>(false);

    const navigate = useRouter();

    const {isAdmin, authToken} = useAuth();

    const {setMessage} = useSiteMessage();

    const profileItemRef = useRef<any>();

    useEffect(() => {
        if (!profileItemRef.current) return;

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setShowImage(true);

                    observer && observer.unobserve(profileItemRef.current);
                }
            })
        })

        observer.observe(profileItemRef.current);
    }, [profileItemRef.current]);

    const onDeleteHandler = async () => {
        const confirmed = window.confirm('Are you sure you want to delete this user?');

        if (confirmed) {

            const doubleConfirmed = window.confirm('Are you really sure?');

            if (!doubleConfirmed) return;

            const response = await deleteAccount(profile.uuid, authToken)

            if (response) {
                deleteProfileCallback && deleteProfileCallback(profile.uuid);

                return;
            }

            setMessage('Failed to delete user', SiteMessageEnum.error);
        }
    };

    const profilePlaceholder = '/imgs/profile-placeholder.png';

    return (
        <Card className="!p-0 relative w-[48%]" style={{overflow: 'hidden'}}>
            <div ref={profileItemRef} className="absolute left-0 flex flex-col gap-2">
                {
                    isAdmin &&
                    <div
                        className="side-button left !relative">
                        <div onClick={onDeleteHandler}>
                            <AiFillDelete className="fill-black"/>
                        </div>
                    </div>
                }
                <div
                    className="side-button left !relative">
                    <div onClick={() => navigate.push(`/u/${profile.username}/message`)}>
                        <AiFillMessage className="fill-black"/>
                    </div>
                </div>
                <div
                    className="side-button left !relative">
                    <div onClick={() => navigate.push(`/u/${profile.username}/galleries`)}>
                        <AiFillPicture className="fill-black"/>
                    </div>
                </div>
            </div>
            <div className="relative flex justify-center align-middle max-w-full min-h-[200px] flex-col w-full">
                    <Link className="w-full mx-auto block p-2" style={{maxWidth: '100%'}}
                          href={`/u/${profile.username}`}>
                        {
                            showImage && (
                                <MediaCenterBox className="!bg-black w-full mx-auto" style={{maxWidth: '100%'}}>
                                    {
                                        profile?.file?.[0]
                                            ? <Img loading="lazy" showLoader={true}
                                                   className="block h-[140px] w-[140px] sm:h-[160px] sm:w-[160px] md:w-[180px] md:h-[180px] lg:w-[200px] lg:h-[200px] rounded-sm mx-auto"
                                                   onError={({currentTarget}: any) => {
                                                       currentTarget.src = profilePlaceholder;
                                                   }} src={imagePathFromFile(profile.file[0])} alt="profile"/>
                                            : <Img loading="lazy" showLoader={true}
                                                   className="block h-[140px] w-[140px] sm:h-[160px] sm:w-[160px] md:w-[180px] md:h-[180px] lg:w-[200px] lg:h-[200px] rounded-sm mx-auto"
                                                   src={profilePlaceholder as unknown as string}
                                                   alt="profile placeholder"/>
                                    }
                                </MediaCenterBox>
                            )
                        }
                    </Link>
                <ProfileBadges showOnEmpty={true} title="badges" badgeWidth={100} maxBadgesCount={6}
                               className="w-full absolute bottom-0 z-[10]" metadata={profile?.metadata} username={profile?.username}/>
                <div className="text-center max-w-full break-words">
                    {profile.username}
                    {profile?.createdAt && <span
                        className="block text-xs text-primary">Joined {(new Date(profile.createdAt)).toLocaleDateString()}</span>}
                </div>
            </div>
            {
                profile?.memberships && profile?.memberships?.length > 0 &&
                <div className="flex flex-wrap justify-center align-middle">
                    {
                        profile.memberships.map((membership, index) => {
                            const endDate = new Date(membership.endDate);

                            const now = new Date();

                            const expiredMembership = now.getTime() > endDate.getTime();

                            if (expiredMembership || !membership.isActive) return null;

                            return <Link key={membership.uuid} href="/billing"><span
                                className="m-1 text-xs rounded-full px-2 bg-secondary whitespace-nowrap">{membershipPlanToName(membership?.productId)}</span></Link>
                        })
                    }
                </div>
            }
        </Card>

    )
}